import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';

export function useAffiliateHistory(walletAddress) {
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [canRefresh, setCanRefresh] = useState(true);
  const walletAddressRef = useRef(walletAddress);

  // walletAddress가 변경될 때마다 ref 업데이트
  useEffect(() => {
    walletAddressRef.current = walletAddress;
  }, [walletAddress]);

  // MSU API에서 최신 데이터 동기화
  const syncData = useCallback(async () => {
    try {
      await supabase.functions.invoke('sync-affiliate-history');
    } catch (err) {
      console.error('Error syncing data:', err);
    }
  }, []);

  // Supabase DB에서 직접 데이터 조회
  const fetchHistories = useCallback(async () => {
    const currentWallet = walletAddressRef.current;

    if (!currentWallet) {
      return;
    }

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('affiliate_histories')
        .select('*')
        .eq('buyer_wallet_address', currentWallet)
        .order('trade_at', { ascending: false });

      if (error) {
        throw error;
      }

      // 데이터 변환 (UI에서 사용하기 쉽게)
      const transformedHistories = (data || []).map((history) => ({
        id: history.affiliate_history_id,
        token_id: history.token_id,
        item_name: history.category || `Item #${history.token_id}`,
        price: history.trade_price, // 이미 KLAY 단위로 저장됨
        quantity: history.trade_quantity,
        fee: history.trade_fee, // 이미 KLAY 단위로 저장됨
        status: 'completed',
        purchase_date: history.trade_at,
        transaction_hash: history.transaction_hash,
      }));

      setHistories(transformedHistories);
      setError(null);
    } catch (err) {
      console.error('Error fetching affiliate histories:', err);
      setError(err.message);
      setHistories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 수동 새로고침 함수
  const refresh = useCallback(async () => {
    if (!canRefresh) {
      return;
    }

    if (!walletAddressRef.current) {
      return;
    }

    setCanRefresh(false);
    setLastRefreshTime(Date.now());

    await syncData();
    await fetchHistories();

    // 1분(60000ms) 후에 다시 새로고침 가능
    setTimeout(() => {
      setCanRefresh(true);
    }, 60000);
  }, [canRefresh, syncData, fetchHistories]);

  useEffect(() => {
    if (!walletAddress) {
      setHistories([]);
      setLoading(false);
      return;
    }

    // 초기 로드 (한 번만)
    const initialize = async () => {
      await syncData();
      await fetchHistories();
      setLastRefreshTime(Date.now());
    };

    initialize();
  }, [walletAddress]);

  return { histories, loading, error, refresh, canRefresh, lastRefreshTime };
}
